import { POSTHOG_BROWSER_DISTINCT_ID_COOKIE } from "@award-party/shared";
import posthog from "posthog-js";
import {
  type AnalyticsConsent,
  persistAnalyticsConsent,
  shouldEnablePostHog,
} from "@/lib/analytics-consent";
import { dropPostHogSafariNetworkLoadException } from "@/lib/safari-network-load-error";

const POSTHOG_DISTINCT_ID_COOKIE_MAX_AGE = 60 * 60 * 24 * 365;

const SENSITIVE_PROFILE_FIELD_KEYWORDS = [
  "password",
  "email",
  "username",
  "user-name",
  "name",
  "first-name",
  "last-name",
  "full-name",
  "phone",
  "tel",
  "profile",
] as const;

const SENSITIVE_PROFILE_AUTOCOMPLETE_VALUES = new Set([
  "name",
  "honorific-prefix",
  "given-name",
  "additional-name",
  "family-name",
  "honorific-suffix",
  "nickname",
  "username",
  "new-password",
  "current-password",
  "one-time-code",
  "organization-title",
  "email",
  "tel",
  "tel-national",
  "tel-area-code",
  "tel-local",
  "tel-local-prefix",
  "tel-local-suffix",
  "tel-extension",
]);

let posthogInitialized = false;

function isLocalhostHostname(hostname: string): boolean {
  return (
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname === "[::1]" ||
    hostname === "0.0.0.0" ||
    hostname.endsWith(".localhost")
  );
}

export function shouldEnablePostHogForEnvironment(): boolean {
  if (process.env.NODE_ENV !== "production") {
    return false;
  }
  if (typeof window === "undefined") {
    return true;
  }
  return !isLocalhostHostname(window.location.hostname);
}

function repeatMask(text: string) {
  return "*".repeat(text.length);
}

function getNormalizedAttribute(
  element: HTMLElement | null | undefined,
  attribute: string,
) {
  return element?.getAttribute(attribute)?.toLowerCase() ?? "";
}

function shouldMaskSensitiveProfileField(
  element: HTMLElement | null | undefined,
) {
  const type = getNormalizedAttribute(element, "type");
  if (type === "password") {
    return true;
  }

  const autocomplete = getNormalizedAttribute(element, "autocomplete");
  if (SENSITIVE_PROFILE_AUTOCOMPLETE_VALUES.has(autocomplete)) {
    return true;
  }

  const attributeValues = [
    getNormalizedAttribute(element, "id"),
    getNormalizedAttribute(element, "name"),
    getNormalizedAttribute(element, "placeholder"),
    getNormalizedAttribute(element, "aria-label"),
    getNormalizedAttribute(element, "data-testid"),
  ];

  return attributeValues.some((value) =>
    SENSITIVE_PROFILE_FIELD_KEYWORDS.some((keyword) => value.includes(keyword)),
  );
}

export function getPostHogBrowserInitOptions() {
  return {
    api_host: process.env.NEXT_PUBLIC_POSTHOG_HOST!,
    ui_host: "https://us.posthog.com",
    defaults: "2026-01-30" as const,
    capture_exceptions: true,
    before_send: dropPostHogSafariNetworkLoadException,
    session_recording: {
      maskAllInputs: false,
      maskInputOptions: {
        password: true,
        email: true,
        text: true,
        tel: true,
        textarea: true,
      },
      maskInputFn: (text: string, element?: unknown) =>
        shouldMaskSensitiveProfileField(element as HTMLElement | null)
          ? repeatMask(text)
          : text,
    },
    debug: process.env.NODE_ENV === "development",
  };
}

export function resetPostHogBrowserStateForTests() {
  posthogInitialized = false;
}

export function initPostHogIfAccepted(
  consent: AnalyticsConsent | null,
): boolean {
  if (
    !shouldEnablePostHog(consent) ||
    !shouldEnablePostHogForEnvironment() ||
    posthogInitialized
  ) {
    return false;
  }

  posthog.init(
    process.env.NEXT_PUBLIC_POSTHOG_KEY!,
    getPostHogBrowserInitOptions(),
  );
  posthogInitialized = true;
  syncPostHogBrowserDistinctIdCookie();
  return true;
}

export function syncPostHogBrowserDistinctIdCookie(): void {
  if (typeof document === "undefined") {
    return;
  }
  try {
    const client = posthog as {
      get_distinct_id?: () => string;
    };
    if (typeof client.get_distinct_id !== "function") {
      return;
    }
    const distinctId = client.get_distinct_id();
    if (typeof distinctId !== "string") {
      return;
    }
    const trimmed = distinctId.trim();
    if (!trimmed || trimmed.length > 200) {
      return;
    }
    document.cookie = `${POSTHOG_BROWSER_DISTINCT_ID_COOKIE}=${encodeURIComponent(trimmed)}; Path=/; Max-Age=${POSTHOG_DISTINCT_ID_COOKIE_MAX_AGE}; SameSite=Lax`;
  } catch {
    // Cookie sync must not break analytics.
  }
}

function disableLoadedPostHog() {
  if (!posthogInitialized) {
    return;
  }
  try {
    posthog.stopSessionRecording();
  } catch {
    // Replay may not be running.
  }
  try {
    posthog.opt_out_capturing();
  } catch {
    // Opt-out should never break the product.
  }
  try {
    posthog.reset();
  } catch {
    // Reset should never break the product.
  }
}

export function applyAnalyticsConsent(consent: AnalyticsConsent): void {
  persistAnalyticsConsent(consent);

  if (consent === "accepted") {
    initPostHogIfAccepted("accepted");
    if (!posthogInitialized) {
      return;
    }
    try {
      posthog.opt_in_capturing();
    } catch {
      // Init already enables capturing for first-time accept.
    }
    try {
      posthog.startSessionRecording();
    } catch {
      // Recording starts with init; this covers a later re-accept.
    }
    syncPostHogBrowserDistinctIdCookie();
    return;
  }

  disableLoadedPostHog();
}

export function shouldIdentifyPostHogUser({
  consent,
  isLoaded,
  userId,
}: {
  consent: AnalyticsConsent | null;
  isLoaded: boolean;
  userId: string | null;
}): boolean {
  return shouldEnablePostHog(consent) && isLoaded && Boolean(userId);
}
